import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, notification } from 'tea-component';
import RichTextEdit from './RichTextEdit';
import RichBodyBox from './RichBodyBox';
import { SummaryTabItem } from './types';
import useLanguage from '@op/hooks/useLanguage';

export type SubmitValueType = { text: string; key: string }[];
export type SubmitClickType = (item: SubmitValueType) => Promise<boolean>;

export default function RichTextEditModal({
  visible,
  title,
  hasTabs,
  defaultActiveTab,
  options,
  canEmptyCommit,
  close,
  submit,
  successCallback,
  errorCallback,
}: {
  visible: boolean;
  title?: string | JSX.Element;
  hasTabs: boolean;
  defaultActiveTab: string;
  options: Array<SummaryTabItem>;
  canEmptyCommit: boolean;
  close?: () => void;
  submit?: SubmitClickType;
  successCallback?: (item: SubmitValueType) => void;
  errorCallback?: () => void;
}) {
  const { formatMessage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [submitCache, setSubmitCache] = useState<{
    [key: string]: { text: string; isEmpty: boolean };
  }>({});

  function onChange(
    value: { text: string; isEmpty: boolean },
    item: SummaryTabItem,
  ) {
    setSubmitCache((submitCache) => ({ ...submitCache, [item.value]: value }));
  }

  useEffect(() => {
    if (defaultActiveTab !== activeTab) {
      setActiveTab(defaultActiveTab);
    }
  }, [visible, defaultActiveTab]);

  function getIsEmpty() {
    return Object.keys(submitCache).some((key) => {
      const item = submitCache[key];
      return item.isEmpty;
    });
  }

  const isEmpty = useMemo(() => {
    return getIsEmpty();
  }, [submitCache]);

  async function submitClick() {
    const isEmpty = getIsEmpty();

    if (!canEmptyCommit && isEmpty) return;

    const data: SubmitValueType = Object.keys(submitCache).map((key) => {
      const { text } = submitCache?.[key] || {};
      return { text, key };
    }, {});

    setLoading(true);
    const flag = await submit?.(data);
    setLoading(false);

    if (flag) {
      notification.success({
        description: formatMessage('Saved successfully'),
      });
      successCallback?.(data);
      cancel();
    } else {
      notification.error({ description: formatMessage('Saved failed') });
      errorCallback?.();
    }
  }

  function cancel() {
    close?.();
    setSubmitCache({});
  }

  return (
    <Modal
      visible={visible}
      size={965}
      caption={title}
      onClose={() => cancel?.()}
    >
      <Modal.Body>
        <RichBodyBox
          hasTabs={hasTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          options={options}
        >
          {(item) => {
            return (
              <RichTextEdit
                item={item}
                key={item.value}
                onChange={(value) => onChange(value, item)}
              ></RichTextEdit>
            );
          }}
        </RichBodyBox>
      </Modal.Body>
      <Modal.Footer>
        <Button
          loading={loading}
          disabled={!canEmptyCommit && isEmpty}
          type="primary"
          onClick={() => void submitClick()}
        >
          {formatMessage('Confirm')}
        </Button>
        <Button type="weak" onClick={() => cancel?.()}>
          {formatMessage('Cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
